import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const forgotSchema = z.object({
  email: z
    .string()
    .min(1, "Email is required")
    .email("Enter a valid email address")
    .max(254),
});

export default function ForgotPassword() {
  const [submitted, setSubmitted] = useState(false);
  const form = useForm({
    resolver: zodResolver(forgotSchema),
    defaultValues: { email: "" },
    mode: "onBlur",
  });

  const onSubmit = async ({ email }) => {
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="min-h-screen w-full flex bg-cream relative overflow-hidden">
      <img
        src="/brand/login-hero.jpg"
        alt=""
        aria-hidden="true"
        className="lg:hidden absolute inset-0 w-full h-full object-cover object-center z-0"
      />
      <div
        className="lg:hidden absolute inset-0 z-0 pointer-events-none bg-gradient-to-br from-brand-dark/35 via-brand/15 to-brand-dark/55"
        aria-hidden="true"
      />

      <div className="relative hidden lg:block lg:flex-1 overflow-hidden">
        <img
          src="/brand/login-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-brand-dark/10 pointer-events-none" />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center px-4 py-10 lg:px-6">
        <div
          className="w-full max-w-md bg-white/95 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 lg:bg-transparent lg:backdrop-blur-none lg:shadow-none lg:rounded-none lg:p-0"
          data-testid="forgot-password-card"
        >
          <div className="flex justify-center mb-6">
            <img
              src="/brand/logo.png"
              alt="Constantiaberg Haematology"
              className="object-contain w-auto"
              style={{ maxWidth: "200px", maxHeight: "72px" }}
            />
          </div>

          <div className="text-center mb-8">
            <h1 className="font-serif text-3xl font-semibold tracking-tight text-zinc-900">
              Forgot your password?
            </h1>
            <p className="text-sm text-neutralsoft mt-1 font-sans">
              We'll email you a secure link to reset it.
            </p>
          </div>

          {!submitted ? (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
                noValidate
                data-testid="forgot-form"
              >
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-medium text-zinc-700">Email address</FormLabel>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 z-10" />
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="you@constantiaberg.local"
                          className="pl-10 h-11"
                          data-testid="forgot-email-input"
                          {...field}
                        />
                      </FormControl>
                    </div>
                    <FormMessage data-testid="forgot-email-error" />
                  </FormItem>
                )} />

                <Button
                  type="submit"
                  disabled={form.formState.isSubmitting}
                  className="w-full h-11 bg-brand hover:bg-brand-dark text-white"
                  data-testid="forgot-submit-btn"
                >
                  {form.formState.isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Send reset link
                </Button>
              </form>
            </Form>
          ) : (
            <div
              className="rounded-xl border border-brand-100 bg-brand-50 p-5 text-center"
              data-testid="forgot-success"
            >
              <CheckCircle2 className="w-7 h-7 text-brand mx-auto mb-2" />
              <p className="text-sm text-brand-dark font-medium">
                If an account exists for that email, a reset link has been sent.
              </p>
              <p className="text-xs text-brand-dark/80 mt-2">
                Check your inbox (and spam folder). The link expires in 1 hour.
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-xs text-brand hover:text-brand-dark font-medium inline-flex items-center gap-1"
              data-testid="forgot-back-to-login"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
